import { createElement } from './helpers';

class eikaTooltip {
    private tooltipElements: NodeListOf<HTMLElement>;

    private currentTooltip: HTMLElement | null;

    public toolTipArrowSize: number = 16;

    constructor() {
        this.tooltipElements = document.querySelectorAll('.esc-tooltip');
        this.currentTooltip = null;
        this.addGlobalListener();
    }

    public addTooltips(): void {
        if (this.tooltipElements) {
            this.initTooltips();
        }
    }

    private createTooltip(id: string, contents: string): HTMLElement {
        const tooltip = createElement('div', {
            class: 'esc-tooltip-content sgw-tooltip__content-wrapper',
            role: 'tooltip',
            id: `${id}-contents`,
        });
        const tooltipContent = createElement(
            'div',
            {
                class: 'sgw-tooltip__content typography-small',
            },
            contents,
        );
        const tooltipArrow = createElement('div', {
            class: 'esc-tooltip-arrow',
            'aria-hidden': 'true',
            style: `--arrow-size: ${this.toolTipArrowSize}px`,
        });
        tooltip.appendChild(tooltipContent);
        tooltip.appendChild(tooltipArrow);
        document.body.appendChild(tooltip);

        return tooltip;
    }

    private initTooltips(): void {
        this.tooltipElements.forEach((tooltip) => {
            const tooltipContents = tooltip.getAttribute('data-tooltip');
            const tooltipId = tooltip.getAttribute('data-tooltip-id');

            if (tooltipId) {
                let theTooltip = document.getElementById(tooltipId);
                if (!theTooltip && tooltipContents) {
                    theTooltip = this.createTooltip(tooltipId, tooltipContents);
                }
                tooltip.setAttribute('aria-describedby', `${tooltipId}-contents`);
                tooltip.setAttribute('aria-expaned', 'false');
                tooltip.removeAttribute('data-tooltip-id');
                this.addClickEvent(tooltip as HTMLButtonElement);
                this.addFocusEvent(tooltip as HTMLButtonElement);
            }
        });
    }

    public static getTooltip(button: HTMLButtonElement): HTMLElement | null {
        const currentId = button.getAttribute('aria-describedby');
        if (!currentId) return null;

        return document.getElementById(currentId);
    }

    private showTooltip(tooltip: HTMLElement, button: HTMLElement): void {
        this.currentTooltip = tooltip;
        this.adjustTooltipPosition(tooltip, button);
        button.setAttribute('aria-expanded', 'true');
        tooltip.classList.add('esc-tooltip-content--visible');
    }

    private closeActiveTooltip(): void {
        if (this.currentTooltip) {
            const btnId = this.currentTooltip.id;
            if (btnId) {
                const button = document.querySelector(`[aria-describedby="${btnId}"]`);
                if (button) {
                    button.setAttribute('aria-expanded', 'false');
                }
            }
            this.currentTooltip.classList.remove('esc-tooltip-content--visible');
            this.currentTooltip = null;
        }
    }

    private addClickEvent(button: HTMLButtonElement): void {
        button.addEventListener('click', () => {
            const tooltip = eikaTooltip.getTooltip(button);
            if (tooltip) {
                this.showTooltip(tooltip, button);
            }
        });

        document.addEventListener('click', (event: MouseEvent) => {
            if (this.currentTooltip) {
                const targetElement = event.target as Element;
                const clickedInsideButton = targetElement.closest('.esc-tooltip');
                const clickedInsideTooltip = targetElement.closest('.esc-tooltip-content');

                if (!clickedInsideButton && !clickedInsideTooltip) {
                    this.currentTooltip.classList.remove('esc-tooltip-content--visible');
                    this.currentTooltip = null;
                }
            }
        });
    }

    private addFocusEvent(button: HTMLButtonElement): void {
        button.addEventListener('focus', () => {
            const tooltip = eikaTooltip.getTooltip(button);
            if (tooltip) {
                this.showTooltip(tooltip, button);
            }
        });
        button.addEventListener('blur', () => {
            this.closeActiveTooltip();
        });
    }

    private addGlobalListener(): void {
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.closeActiveTooltip();
            }
        });
    }

    private adjustTooltipPosition(tooltipContent: HTMLElement, button: HTMLElement): void {
        const buttonRect = button.getBoundingClientRect();
        const tooltipRect = tooltipContent.getBoundingClientRect();
        const arrow = tooltipContent.querySelector('.esc-tooltip-arrow') as HTMLDivElement;
        const arrowOffset = this.toolTipArrowSize / 2;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const centeredLeft = buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2;
        let topPosition = buttonRect.bottom + window.scrollY + arrowOffset;

        tooltipContent.style.left = `${centeredLeft}px`;
        if (arrow) {
            arrow.setAttribute('class', 'esc-tooltip-arrow esc-tooltip-arrow--top');
        }

        if (centeredLeft + tooltipRect.width > viewportWidth) {
            tooltipContent.style.left = `${viewportWidth - tooltipRect.width - 10}px`;
            if (arrow) {
                arrow.style.left = `auto`;
                arrow.style.right = `${arrowOffset}px`;
            }
        }

        if (centeredLeft < 0) {
            tooltipContent.style.left = `10px`;
            if (arrow) {
                arrow.style.left = `${arrowOffset}px`;
            }
        }

        if (topPosition + tooltipRect.height > viewportHeight) {
            topPosition = buttonRect.top + window.scrollY - tooltipRect.height - arrowOffset;
            if (arrow) {
                arrow.setAttribute('class', 'esc-tooltip-arrow esc-tooltip-arrow--bottom');
            }
        }
        tooltipContent.style.top = `${topPosition}px`;
    }
}

function EikaTooltips(): void {
    document.addEventListener('DOMContentLoaded', () => {
        const Tooltips = new eikaTooltip();
        Tooltips.addTooltips();
    });
}

export default EikaTooltips;
